import { memo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { messageTypes, addNegativeFeedbackMessage, askLocalQuestion } from 'store/modules/chat-messages';
import { finishedTutorial, completeFirstAccessTutorial } from 'store/modules/tutorial';
import { giveFeedBackComment } from 'store/modules/recentFeedback';
import useMediaQuery from 'utils/mediaQueries';
import { tutorialNames } from 'config/constants';

import QuestionPills from 'components/pills/QuestionPills';

import Input from 'components/input/Input';
import FollowUpInfo from 'components/pills/FollowUpInfo';

const MessageComposer = ({
  followUpState,
  user,
  recentFeedback,
  isLastMessageHidden,
  hasFinishedFirstAccessTutorial,
  uncompletedTutorials
}) => {
  const isMobile = useMediaQuery();
  const dispatch = useDispatch();
  const { t } = useTranslation('veezoo');

  const processStandardFeedback = text => {
    dispatch(addNegativeFeedbackMessage('veezoo', recentFeedback));
    dispatch(giveFeedBackComment(recentFeedback.answerId, text));
  };

  // Either stores a negative feedback if in negative feedback mode, or asks a question
  const dispatchMessageAndReset = (
    message,
    isQuestionPill,
    isLlmSuggestion,
    isSuggested,
    isVoice,
    linkedEntities,
    answerId,
    interpretationId
  ) => {
    // don't dispatch an empty message
    if (message === '') return true;
    if (recentFeedback.wasNegative) {
      // Just handle standard negative feedback: show a message, log in backend, reset the feedback state, then return
      return processStandardFeedback(message);
    }

    let referenceTo = null;
    let disableFollowUpParam = {};

    // if reference to answerId and possibly interpretationId exists for pill
    if (answerId !== undefined) {
      referenceTo = {
        answerId,
        interpretationId
      };
    } else if (followUpState) {
      // if question is followUp
      referenceTo = followUpState;
    } else if (isLastMessageHidden) {
      // if last message is hidden and we're not in an explicit follow-up
      disableFollowUpParam = { disableFollowUp: true };
    }

    const otherParams = {
      isQuestionPill,
      isLlmSuggestion,
      isSuggested,
      isVoice,
      linkedEntities,
      referenceTo,
      ...disableFollowUpParam
    };

    dispatch(askLocalQuestion(user.username, message, otherParams, t));
  };

  const onSubmit = (message, isSuggestion, isVoice, linkedEntities) => {
    dispatchMessageAndReset(message, false, false, isSuggestion, isVoice, linkedEntities);
  };

  const dismissFirstAccessTutorial = () => {
    if (!hasFinishedFirstAccessTutorial) {
      dispatch(completeFirstAccessTutorial());
    }
    if (uncompletedTutorials.some(tutorial => tutorial.id === tutorialNames.OVERVIEW_TUTORIAL)) {
      dispatch(finishedTutorial(tutorialNames.OVERVIEW_TUTORIAL, true));
    }
  };

  return (
    <div
      id="message-composer"
      data-overview-tutorial="message-composer"
      data-whatcaniask-tutorial="message-composer"
      onClick={dismissFirstAccessTutorial}
    >
      {followUpState && <FollowUpInfo followUpState={followUpState} t={t} />}
      <QuestionPills
        processStandardFeedback={processStandardFeedback}
        dispatchMessageAndReset={dispatchMessageAndReset}
      />
      <Input onSubmit={onSubmit} isMobile={isMobile} />
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.user,
  hasFinishedFirstAccessTutorial: state.tutorial.hasFinishedFirstAccessTutorial,
  uncompletedTutorials: state.tutorial.uncompletedTutorials,
  isLastMessageHidden: state.chatMessages[state.chatMessages.length - 1]?.type === messageTypes.USER_HIDDEN_MESSAGE,
  followUpState: state.followUpState,
  recentFeedback: state.recentFeedback
});

export default connect(mapStateToProps)(memo(MessageComposer));
